import { IOfferDefaultConfiguration, IRegularPaymentCondition } from '../interfaces/IOfferItem';
import {
    ICallToAction,
    IPointOfSale,
    ISpecification,
    IStockSpecificFields,
} from '../services/stock/models/stockItems.service.model';
import { BrandTypes } from '../types/brands';
import { IImage, IPossibleOption, IPrice, IPriceV2 } from '../services';
import { IPromotionalText } from './Price';
import { IDealerFee } from '@components/DealerFees/DealerFeesTemplate';
import { IDealExtraFields } from '../redux/user/user.duck.interface';
import { TBudgetType } from './IFilters';
import { IAccessory } from '../services/carDetails/carDetails.types';

export interface IFeatureExtended {
    description: string;
    id: string;
    title: string;
    images: IImage[];
    prices: IPrice[];
    specs: ISpecification[];
}

export interface ICarImage {
    default: CarImageDefaults[];
    type: CarImageTypes;
    url: string;
}

export interface IPartSpec<K extends string = ''> {
    key: K;
    label: string;
    value: string | number;
}

export interface ICarPart<SK extends string = ''> {
    title: string;
    description: string;
    id: string;
    images: string[];
    specs: IPartSpec<SK>[];
}

export interface ICarFeature {
    order: number;
    category: string;
    key: string;
    data: any[] | any; // todo generic
}

export interface ICarExtraFields {
    tariffCode: string;
    seriesEquipment: ICarFeature[];
    grBodyStyle: any;
    grGearbox: any;
    defaultConfiguration?: IOfferDefaultConfiguration;
    regularPaymentConditions?: IRegularPaymentCondition[];
    promotionalText?: IPromotionalText;
    pricesV2: IPriceV2[];
    visualCode?: string;
    leadTimeDate?: string | Date;
    isDefaultConfiguration?: {
        cash: boolean;
        finance: boolean;
    };
    vehicleUseId?: string;
    leadTimeProductionYear?: number;
    possibleOptions?: IPossibleOption[];
    tariffStartDate?: string;
    displayGrBodyStyleLabel?: boolean;
    maxLeadTimeProductionYear?: number;
    nameplateBodyStyle?: string;
    maxLeadTimeDate?: string | Date;
    disclaimer?: any[];
}

export type TOfferItem = {
    losangeLabel: string;
    popupText: string;
};

export type TBenefitItem = {
    benefitTitle: string;
    benefitText: string;
};

export type TBenefit = TOfferItem | TBenefitItem;

export const isBenefitItem = (item: TBenefit): item is TBenefitItem => {
    return (item as TBenefitItem).benefitTitle !== undefined;
};

interface IBenefits {
    cash: TBenefit;
    finance: TBenefit;
}

export interface ICarFee {
    id: string;
    title: string;
    priceInclTax: number;
}

export interface ICarAccessory {
    id: string;
    title: string;
    priceInclTax: number;
    selected?: boolean;
    label?: string;
    netAmount?: number;
}

export interface ICarComment {
    text: string;
    type: string;
}

export interface IBaseCarOffer {
    brand: string;
    campaignId: number;
    campaignType: string;
    id: number;
    losangeLabel: string;
    popupHeader: string;
    popupText: string;
    priority: number;
    title: string;
    defaultConfiguration?: IOfferDefaultConfiguration;
    dealerLayerEnabled?: boolean;
}

export interface ICarOffer extends IBaseCarOffer {
    // benefits?: IBenefits; APP-7417, change data model, SOL fr = SOL UK ?
    benefits?: TBenefit[] | Record<number, TBenefit>;
    promo?: any;
}

export interface ISOLCarOffer extends IBaseCarOffer {
    benefits?: TBenefit[] | Record<number, TBenefit> | Record<TBudgetType, TBenefit[]>;
}

interface ICar {
    prices: IPrice[];
    language: string;
    title: string;
    interiorColour: any;
    externalId: string;
    fuel: string;
    gearbox: ICarPart<GearboxSpecKeys>;
    bodyStyle: ICarPart; // has no specs
    model: ICarPart; // has no specs
    engine: ICarPart<EngineSpecKeys>;
    lcdv16: string;
    fuelCode: string;
    id: string;
    _id: string;
    exteriorColour: any;
    brand: BrandTypes;
    images: ICarImage[];
    specPack: any;
    extraFields: ICarExtraFields;
    engineGearboxFuelSlug: string;
    exteriorColourSlug: string;
    interiorColourSlug: string;
    displayGrBodyStyleLabel?: boolean;
    specPackSlug: string;
    stock: boolean;
    nameplate: ISpecification;
    nameplateBodyStyleSlug: string;
    stockSpecificFields: IStockSpecificFields;
    market: string;
    accessories?: ICarAccessory[];
    dealers?: IPointOfSale[];
    fees?: IDealerFee[];
    comments?: ICarComment[];
    pricesV2?: IPriceV2[];
    vehicleUseId?: string;
}

export interface IStockCar extends ICar {
    detailsAggregated?: string;
    detailsOptionsAggregated?: string;
    modelYear?: string;
    market: string;
    options: any[];
}

export interface ICarImportInput {
    pimcDiscount: number;
    registrationFeePercentage: number;
    transportationCostInclTax: number;
    transportationCostExclTax: number;
    ecologicalBonus?: number;
    brandEcologicalBonus?: number;
    offerValidityDate?: string;
    tfm?: ITFMItem[];
    accessoriesPrice?: number;
}

export interface ITFMItem {
    code: string;
    label: string;
    amount: number;
    percentage: number;
}

export interface IConfigurableCar extends ICar {
    options: any[];
    maxLeadTime: number;
    maxLeadTimeDate: Date;
    leadTimeDate: Date;
    leadTime: number;
    standardFeatures: ICarFeature[];
    technicalSpecifications: ICarFeature[];
    offers: ICarOffer[] | ISOLCarOffer[];
    characteristics: { key: string; value: any }[];
    importInputs: ICarImportInput;
    dealers: IPointOfSale[];
    orders: IPointOfSale[];
    seriesEquipment?: ICarFeature[];
    gearboxLabel?: string;
    trimAttributes?: ITrimAttribute[];
}

export interface ITrimAttribute {
    category: string;
    attributeCode: string;
    _id: string;
    title: string;
    imageURL: string;
    description: string;
}

export interface IPXSummary {
    prices: {
        dealerValuation: number;
        dealerBonusAmount: number;
        governmentContributionAmount: number;
    };
    vehicle: {
        makeName: string;
        modelName: string;
        vin: string;
        registrationDate: string;
        overallMilleage: number;
        regNumber: string;
    };
    source: string;
    salesman: {
        userId: number;
        name: string;
    };
}

export interface IConfigurableCarExtended extends Omit<IConfigurableCar, 'extraFields' | '_id'> {
    modelYear?: string;
    vehicleUseId: string;
    tariffCode: string;
    tariffStartDate: string;
    visualCode: string;
    nameplateBodyStyle: string;
    possibleOptions: IPossibleOption[];
    seriesEquipment: ICarFeature[];
    grBodyStyle: IFeatureExtended;
    grGearbox: IFeatureExtended;
    detailsAggregated?: string;
    detailsOptionsAggregated?: string;
    importHash: string;
    status: string;
    leadTimeProductionYear: number;
    maxLeadTime: number;
    maxLeadTimeProductionYear: number;
    callToAction: ICallToAction;
    promotionalText?: IPromotionalText;
    noDeposit: boolean;
    isDefaultConfiguration: {
        cash: boolean;
        finance: boolean;
    };
    defaultConfiguration?: IOfferDefaultConfiguration;
    configurationUrl: string;
}

export type EngineSpecKeys = 'fuel' | 'power' | 'taxBand' | 'co2Combined'; // TODO not all
export type GearboxSpecKeys = 'type' | 'rapports';
export type CarImageTypes = 'Ext' | 'Int' | '360';
export type CarImageDefaults = 'selector' | 'list'; // TODO all?

export interface IDeal<ProductConfigurationType = IConfigurableCar & { upsellOptions?: string[] }> {
    checkoutStep: string;
    productType: string;
    financeQuoteId: string;
    saved: boolean;
    configuration: string; // car configuration id
    token: string; // deal id
    fullProductConfiguration: ProductConfigurationType; // full car configuration object
    mopId: string | null; // saved mopId
    journeyType: string;
    dealerId?: string;
    endDate?: string;
    status?: string;
    extraFields?: IDealExtraFields;
    partExchangeId: string;
    // TODO....
}
